import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getTabs, createTab as apiCreateTab, updateTab as apiUpdateTab, deleteTab as apiDeleteTab, deleteAllTabs } from '../services/api';



// Створюємо контекст для табів
const TabsContext = createContext();

/**
 * Provider компонент для контексту табів
 * @param {Object} props - Властивості компонента
 * @param {React.ReactNode} props.children - Дочірні компоненти
 */
export const TabsProvider = ({ children }) => {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Створюємо ref для зберігання актуального activeTabId, щоб уникнути stale closures
  const activeTabIdRef = useRef(activeTabId);
  const tabsRef = useRef(tabs);
  const debounceTimerRef = useRef(null);

  // Оновлюємо ref кожен раз, коли activeTabId змінюється
  useEffect(() => {
    activeTabIdRef.current = activeTabId;
  }, [activeTabId]);

  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  // Активна вкладка
  const [activeTab, setActiveTab] = useState(null);

  // Поточний стан калькулятора для дублювання
  const currentCalculatorStateRef = useRef(null);

  const closeAllTabs = async () => {
    try {
      await deleteAllTabs(); // Call the new API function
      
      // After successful deletion, create a new default tab
      const response = await apiCreateTab({ name: 'Offer #1', settings: {} });
      const newTab = response.data; // Axios wraps the response in a data object

      if (newTab && newTab.id) {
        setTabs([newTab]);
        setActiveTabId(newTab.id);
      } else {
        // Fallback in case the new tab creation fails or returns an unexpected structure
        console.error('Failed to create a default tab or invalid response:', response);
        setTabs([]);
        setActiveTabId(null);
        setError(new Error('Failed to create a default tab after closing all.'));
      }
    } catch (err) {
      console.error('Failed to close all tabs:', err);
      setError(err);
    }
  };



  const createTab = useCallback(async (options = {}) => {
    try {
      // Support both { name, settings } and just settings object
      const { name: customName, settings: customSettings, ...restSettings } = options;
      const finalSettings = customSettings || restSettings || {};
      const newTabName = customName || `Offer #${tabsRef.current.length + 1}`;

      const response = await apiCreateTab({ name: newTabName, settings: finalSettings }); // Backend expects 'settings'
      const newTab = response.data; // Correctly extract tab data from response
      setTabs(prevTabs => [...prevTabs, newTab]);
      setActiveTabId(newTab.id);
      return newTab.id;
    } catch (err) {
      console.error('Failed to create tab:', err);
      setError(err);
      return null;
    }
  }, []);

  const duplicateTab = useCallback(async (tabId, newData = null) => {
    const tabToDuplicate = tabsRef.current.find(tab => tab.id === tabId);
    if (!tabToDuplicate) return null;

    const newTabName = `${tabToDuplicate.name} (copy)`;
    const finalData = newData || { ...tabToDuplicate.settings };
    // Add marker to indicate this is a duplicated tab
    finalData._isDuplicated = true;

    const newTabData = {
      name: newTabName,
      settings: finalData, // Backend expects 'settings', not 'data'
    };

    try {
      const response = await apiCreateTab(newTabData);
      const newTab = response.data; // Correctly extract tab data from response
      setTabs(prevTabs => [...prevTabs, newTab]);
      setActiveTabId(newTab.id);
      return newTab.id;
    } catch (err) {
      console.error('Failed to duplicate tab:', err);
      setError(err);
      return null;
    }
  }, []);

  const renameTab = useCallback(async (tabId, newName) => {
    const tabToUpdate = tabsRef.current.find(tab => tab.id === tabId);
    if (!tabToUpdate) return;

    const updatedTab = { ...tabToUpdate, name: newName };

    try {
      await apiUpdateTab(tabId, updatedTab);
      setTabs(prevTabs =>
        prevTabs.map(tab => (tab.id === tabId ? updatedTab : tab))
      );
    } catch (err) {
      console.error('Failed to rename tab:', err);
      setError(err);
    }
  }, []);

  // Function to duplicate tab with current state
  const duplicateTabWithCurrentState = useCallback(async (tabId, currentState) => {
    return await duplicateTab(tabId, currentState);
  }, [duplicateTab]);

  // Function to reorder tabs
  const reorderTabs = useCallback((newOrder) => {
    setTabs(newOrder);
  }, []);

  // Function to update current calculator state for duplication
  const updateCurrentCalculatorState = useCallback((state) => {
    currentCalculatorStateRef.current = state;
  }, []);

  // Enhanced duplicate function that uses current state if available
  const duplicateActiveTab = useCallback(async () => {
    const currentState = currentCalculatorStateRef.current;
    return await duplicateTab(activeTabIdRef.current, currentState);
  }, [duplicateTab]);

  // Функція для видалення табу
  const deleteTab = useCallback(async (tabId) => {
    const currentTabs = tabsRef.current;
    if (currentTabs.length <= 1) {
      return false;
    }

    try {
      await apiDeleteTab(tabId);

      const newTabs = currentTabs.filter(tab => tab.id !== tabId);
      setTabs(newTabs);

      if (activeTabIdRef.current === tabId) {
        const originalIndex = currentTabs.findIndex(tab => tab.id === tabId);
        const newActiveTab = newTabs[Math.max(0, originalIndex - 1)] || newTabs[0] || null;
        setActiveTabId(newActiveTab?.id);
      }
      return true;
    } catch (err) {
      console.error('Failed to delete tab:', err);
      setError(err);
      return false;
    }
  }, []);

  const updateActiveTabData = useCallback((newData, targetTabIdToUpdate) => {
    const idToUpdate = targetTabIdToUpdate || activeTabIdRef.current;
    if (!idToUpdate) return;

    // Оновлюємо локальний стан негайно для кращого UX
    setTabs(prevTabs => {
      const currentTab = prevTabs.find(t => t.id === idToUpdate);
      if (currentTab && JSON.stringify(currentTab.settings) === JSON.stringify(newData)) {
        return prevTabs;
      }
      return prevTabs.map(tab =>
        tab.id === idToUpdate ? { ...tab, settings: newData } : tab
      );
    });

    // Дебаунс для API-запиту
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      const currentTabs = tabsRef.current;
      const tabToUpdate = currentTabs.find(tab => tab.id === idToUpdate);
      if (!tabToUpdate) return;

      // Дані в `tabToUpdate` можуть бути застарілими, але `newData` з замикання - актуальні.
      const updatedTab = { ...tabToUpdate, settings: newData };

      try {
        await apiUpdateTab(idToUpdate, updatedTab);
      } catch (err) {
        console.error('Failed to save tab data:', err);
        setError(err);
      }
    }, 1000); // Затримка в 1 секунду
  }, []);



  // Fetch tabs from API on initial load
  useEffect(() => {
    const fetchTabs = async () => {
      try {
        setLoading(true);
        const response = await getTabs();
        const serverTabs = response.data;

        if (serverTabs && serverTabs.length > 0) {
          setTabs(serverTabs);

          const params = new URLSearchParams(window.location.search);
          const requestedActiveTabId = params.get('activeTab');
          const requestedTabExists = serverTabs.some(tab => tab.id === requestedActiveTabId);
          setActiveTabId(requestedTabExists ? requestedActiveTabId : serverTabs[0].id);
        } else {
          // If no tabs, create a default one
          const newTabResponse = await apiCreateTab({ name: 'Offer #1', settings: {} });
          const newTab = newTabResponse.data;
          if (newTab) {
            setTabs([newTab]);
            setActiveTabId(newTab.id);
          } else {
             throw new Error("Failed to create a default tab.");
          }
        }
      } catch (err) {
        setError(err);
        console.error('Failed to fetch tabs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTabs();
  }, []);

  // Ефект для оновлення activeTab, коли змінюється activeTabId або tabs
  useEffect(() => {
    const newActiveTab = tabs.find(tab => tab.id === activeTabId);
    setActiveTab(newActiveTab || (tabs.length > 0 ? tabs[0] : null));
  }, [activeTabId, tabs]);

  // Значення контексту
  const contextValue = {
    tabs,
    activeTabId,
    activeTab,
    loading,
    error,
    setActiveTabId,
    createTab,
    duplicateTab,
    duplicateTabWithCurrentState,
    duplicateActiveTab,
    updateCurrentCalculatorState,
    renameTab,
    deleteTab,
    reorderTabs,
    updateActiveTabData,
    closeAllTabs,
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Create new tab: Ctrl + ; or Cmd + ; (using event.code for layout independence)
      if ((event.ctrlKey || event.metaKey) && event.code === 'Semicolon') {
        event.preventDefault();
        createTab();
      }

      // Duplicate active tab: Ctrl + ' or Cmd + ' (using event.code for layout independence)
      if ((event.ctrlKey || event.metaKey) && event.code === 'Quote') {
        event.preventDefault();
        duplicateActiveTab(); // Use enhanced function with current state
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [createTab, duplicateActiveTab]); // Dependencies ensure the functions are up-to-date

  return (
    <TabsContext.Provider value={contextValue}>
      {children}
    </TabsContext.Provider>
  );
};

/**
 * Хук для використання контексту табів
 * @returns {Object} - Значення контексту табів
 */
export const useTabs = () => {
  const context = useContext(TabsContext);
  if (context === undefined) {
    throw new Error('useTabs must be used within a TabsProvider');
  }
  return context;
};

export default TabsContext;
